/**
 * src/shell/i18n/components/LanguageManagerModal.tsx
 *
 * Full-featured Language Manager, Base File Downloader,
 * Interactive 2-Column Translation Editor, and AST JSON Validator Modal.
 */

import React, { useState, useEffect, useRef } from 'react';
import { dynamicI18nEngine, GLOBAL_LANGUAGE_CATALOG } from '../services/DynamicI18nEngine';
import {
  LanguageCatalogEntry,
  TranslationValidationReport,
  TranslationEditRecord,
} from '../types/i18n-workflow-types';

export interface LanguageManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LanguageManagerModal: React.FC<LanguageManagerModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'CATALOG' | 'EDITOR' | 'UPLOAD'>('CATALOG');
  const [installedLanguages, setInstalledLanguages] = useState<LanguageCatalogEntry[]>([]);
  const [availableCatalog, setAvailableCatalog] = useState<LanguageCatalogEntry[]>([]);
  const [activeLocale, setActiveLocale] = useState<string>('en');

  // 2-Column Editor State
  const [editorLocale, setEditorLocale] = useState<string>('es');
  const [records, setRecords] = useState<TranslationEditRecord[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [showMissingOnly, setShowMissingOnly] = useState(false);
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});

  // Upload & Raw JSON State
  const [uploadLocale, setUploadLocale] = useState<string>('es');
  const [rawJsonInput, setRawJsonInput] = useState('');
  const [validationReport, setValidationReport] = useState<TranslationValidationReport | null>(null);

  // Feedback State
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshCatalog = () => {
    setInstalledLanguages(dynamicI18nEngine.getInstalledLanguages());
    setAvailableCatalog(dynamicI18nEngine.getAvailableCatalog());
    setActiveLocale(dynamicI18nEngine.getLocale());
  };

  useEffect(() => {
    refreshCatalog();
    return dynamicI18nEngine.subscribe(() => {
      refreshCatalog();
    });
  }, []);

  // Load editor records whenever editorLocale changes
  useEffect(() => {
    if (activeTab === 'EDITOR') {
      const recs = dynamicI18nEngine.getTranslationRecords(editorLocale);
      setRecords(recs);
      setPendingEdits({});
    }
  }, [editorLocale, activeTab]);

  if (!isOpen) return null;

  // ─── Handlers: Download ───────────────────────────────────────────────────

  const triggerDownload = (fileName: string, content: string, contentType = 'application/json') => {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setFeedbackMessage({ type: 'success', text: `Downloaded ${fileName}` });
  };

  const handleDownloadBase = (locale = 'en') => {
    const exported = dynamicI18nEngine.exportBaseDictionary(locale);
    triggerDownload(exported.fileName, exported.content);
  };

  const handleDownloadTemplate = (targetLocale: string) => {
    const template = dynamicI18nEngine.generateTranslationTemplate(targetLocale);
    triggerDownload(template.fileName, template.content);
  };

  // ─── Handlers: Install / Set Active / Remove ──────────────────────────────

  const handleInstallFromCatalog = async (code: string) => {
    const res = await dynamicI18nEngine.installFromCatalog(code);
    if (res.success) {
      setFeedbackMessage({ type: 'success', text: `Installed ${code.toUpperCase()} language pack.` });
      refreshCatalog();
    } else {
      setFeedbackMessage({ type: 'error', text: res.error || 'Failed to install pack.' });
    }
  };

  const handleSetActive = (code: string) => {
    const success = dynamicI18nEngine.setLocale(code);
    if (success) {
      setActiveLocale(code);
      setFeedbackMessage({ type: 'success', text: `Switched active platform language to ${code.toUpperCase()}.` });
    }
  };

  const handleRemovePack = async (code: string) => {
    const removed = await dynamicI18nEngine.removeLanguagePack(code);
    if (removed) {
      setFeedbackMessage({ type: 'success', text: `Removed ${code.toUpperCase()} language pack.` });
      refreshCatalog();
    }
  };

  // ─── Handlers: 2-Column Editor ────────────────────────────────────────────

  const handleEditChange = (key: string, value: string) => {
    setPendingEdits((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveEditor = async () => {
    const res = await dynamicI18nEngine.updateTranslations(editorLocale, pendingEdits);
    if (res.success) {
      setFeedbackMessage({
        type: 'success',
        text: `Saved ${Object.keys(pendingEdits).length} translations for ${editorLocale.toUpperCase()}.`,
      });
      const recs = dynamicI18nEngine.getTranslationRecords(editorLocale);
      setRecords(recs);
      setPendingEdits({});
      refreshCatalog();
    } else {
      setFeedbackMessage({ type: 'error', text: res.error || 'Failed to save translations.' });
    }
  };

  // ─── Handlers: Upload & Raw JSON ──────────────────────────────────────────

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setRawJsonInput(content);
      const report = dynamicI18nEngine.validateTranslationPayload(uploadLocale, content);
      setValidationReport(report);
    };
    reader.readAsText(file);
  };

  const handleValidateRawJson = () => {
    const report = dynamicI18nEngine.validateTranslationPayload(uploadLocale, rawJsonInput);
    setValidationReport(report);
  };

  const handleApplyUpload = async () => {
    if (!rawJsonInput.trim()) return;
    const res = await dynamicI18nEngine.uploadTranslationFile(uploadLocale, rawJsonInput);
    if (res.success) {
      setFeedbackMessage({
        type: 'success',
        text: `Successfully imported & activated ${uploadLocale.toUpperCase()} with ${res.report?.coveragePct}% coverage.`,
      });
      refreshCatalog();
      setValidationReport(null);
      setRawJsonInput('');
    } else {
      setFeedbackMessage({ type: 'error', text: res.error || 'Failed to import JSON dictionary.' });
    }
  };

  // Filtered editor records
  const filteredRecords = records.filter((r) => {
    const currentValue = pendingEdits[r.key] !== undefined ? pendingEdits[r.key] : r.currentValue;
    if (showMissingOnly && currentValue && currentValue.trim() !== '') {
      return false;
    }
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    return (
      r.key.toLowerCase().includes(q) ||
      r.baseValue.toLowerCase().includes(q) ||
      currentValue.toLowerCase().includes(q)
    );
  });

  return (
    <div
      data-testid="language-manager-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 110,
        background: 'rgba(5, 10, 20, 0.85)',
        backdropFilter: 'blur(16px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        color: '#f8fafc',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '1080px',
          maxHeight: '90vh',
          background: 'rgba(15, 23, 42, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: '24px',
          padding: '28px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 40px rgba(56, 189, 248, 0.15)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            paddingBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px',
                boxShadow: '0 4px 14px rgba(14, 165, 233, 0.3)',
              }}
            >
              🌐
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>
                Language &amp; Translation Workflow
              </h2>
              <p style={{ fontSize: '12px', color: '#94a3b8', margin: '2px 0 0 0' }}>
                Base file exports, inline 2-column editor, JSON key validation &amp; catalog discovery
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              data-testid="btn-download-base-en"
              onClick={() => handleDownloadBase('en')}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid rgba(56, 189, 248, 0.3)',
                background: 'rgba(56, 189, 248, 0.1)',
                color: '#38bdf8',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
              title="Download canonical en.json baseline"
            >
              📥 Download Base (en.json)
            </button>

            <button
              type="button"
              data-testid="btn-close-lang-manager"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                color: '#94a3b8',
                padding: '6px 12px',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Close ✕
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '18px',
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '4px',
            borderRadius: '12px',
            width: 'fit-content',
          }}
        >
          {[
            { id: 'CATALOG', label: 'Language Catalog' },
            { id: 'EDITOR', label: '2-Column Editor' },
            { id: 'UPLOAD', label: 'Upload / Paste JSON' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`tab-i18n-${t.id.toLowerCase()}`}
              onClick={() => {
                setActiveTab(t.id as any);
                setFeedbackMessage(null);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: activeTab === t.id ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                color: activeTab === t.id ? '#38bdf8' : '#94a3b8',
                fontWeight: 700,
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Feedback Alert Toast */}
        {feedbackMessage && (
          <div
            data-testid="i18n-feedback-banner"
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '12px',
              fontWeight: 600,
              background:
                feedbackMessage.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              border: `1px solid ${
                feedbackMessage.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'
              }`,
              color: feedbackMessage.type === 'success' ? '#6ee7b7' : '#fca5a5',
            }}
          >
            {feedbackMessage.type === 'success' ? '✓' : '⚠️'} {feedbackMessage.text}
          </div>
        )}

        {/* ─── TAB 1: CATALOG & PACKS ───────────────────────────────────────── */}
        {activeTab === 'CATALOG' && (
          <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
            {/* Installed Section */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', margin: '0 0 12px 0' }}>
                Installed Languages ({installedLanguages.length})
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
                {installedLanguages.map((lang) => (
                  <div
                    key={lang.code}
                    data-testid={`card-installed-lang-${lang.code}`}
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: `1px solid ${lang.isActive ? 'rgba(56, 189, 248, 0.4)' : 'rgba(255, 255, 255, 0.08)'}`,
                      borderRadius: '14px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>{lang.flagEmoji}</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff' }}>
                            {lang.name} ({lang.code})
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>{lang.nativeName}</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '6px' }}>
                        {lang.direction === 'rtl' && (
                          <span style={{ fontSize: '10px', background: 'rgba(168, 85, 247, 0.2)', color: '#d8b4fe', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                            RTL
                          </span>
                        )}
                        {lang.isActive && (
                          <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.2)', color: '#6ee7b7', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>
                            Active
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Coverage Bar */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
                        <span>Coverage: {lang.coveragePct}%</span>
                        <span>{lang.keyCount} keys</span>
                      </div>
                      <div style={{ height: '4px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${lang.coveragePct}%`,
                            background: lang.coveragePct > 80 ? '#10b981' : lang.coveragePct > 40 ? '#f59e0b' : '#ef4444',
                          }}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                      <button
                        type="button"
                        data-testid={`btn-edit-lang-${lang.code}`}
                        onClick={() => {
                          setEditorLocale(lang.code);
                          setActiveTab('EDITOR');
                        }}
                        style={{
                          flex: 1,
                          padding: '6px',
                          borderRadius: '6px',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          background: 'rgba(255, 255, 255, 0.05)',
                          color: '#fff',
                          fontSize: '11px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        ✏️ Edit
                      </button>

                      <button
                        type="button"
                        data-testid={`btn-download-lang-${lang.code}`}
                        onClick={() => handleDownloadBase(lang.code)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          background: 'rgba(255, 255, 255, 0.05)',
                          color: '#94a3b8',
                          fontSize: '11px',
                          cursor: 'pointer',
                        }}
                        title="Download JSON"
                      >
                        📥
                      </button>

                      {!lang.isActive && (
                        <button
                          type="button"
                          data-testid={`btn-activate-lang-${lang.code}`}
                          onClick={() => handleSetActive(lang.code)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            background: 'rgba(16, 185, 129, 0.15)',
                            color: '#6ee7b7',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Activate
                        </button>
                      )}

                      {lang.code !== 'en' && lang.isCustom && (
                        <button
                          type="button"
                          data-testid={`btn-remove-lang-${lang.code}`}
                          onClick={() => handleRemovePack(lang.code)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            background: 'rgba(239, 68, 68, 0.15)',
                            color: '#fca5a5',
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                          title="Remove language pack"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Available Catalog Section */}
            {availableCatalog.length > 0 && (
              <div>
                <h3 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', margin: '0 0 12px 0' }}>
                  Available in Global Catalog ({availableCatalog.length})
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                  {availableCatalog.map((lang) => (
                    <div
                      key={lang.code}
                      data-testid={`card-available-lang-${lang.code}`}
                      style={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px dashed rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px' }}>{lang.flagEmoji}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '12px', color: '#cbd5e1' }}>
                            {lang.name} ({lang.code})
                          </div>
                          <div style={{ fontSize: '10px', color: '#64748b' }}>{lang.nativeName}</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          data-testid={`btn-download-template-${lang.code}`}
                          onClick={() => handleDownloadTemplate(lang.code)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            background: 'rgba(255, 255, 255, 0.04)',
                            color: '#94a3b8',
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                          title="Download empty template"
                        >
                          Template 📥
                        </button>

                        <button
                          type="button"
                          data-testid={`btn-install-lang-${lang.code}`}
                          onClick={() => handleInstallFromCatalog(lang.code)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          Install ⚡
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── TAB 2: 2-COLUMN TRANSLATION EDITOR ────────────────────────────── */}
        {activeTab === 'EDITOR' && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {/* Editor Toolbar */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                marginBottom: '14px',
                background: 'rgba(255, 255, 255, 0.02)',
                padding: '10px 14px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.06)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>Editing Locale:</label>
                <select
                  data-testid="select-editor-locale"
                  value={editorLocale}
                  onChange={(e) => setEditorLocale(e.target.value)}
                  style={{
                    background: '#0f172a',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    color: '#fff',
                    fontSize: '12px',
                  }}
                >
                  {installedLanguages.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.flagEmoji} {l.name} ({l.code})
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  data-testid="input-editor-search"
                  placeholder="Search keys or text…"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '6px 10px',
                    color: '#fff',
                    fontSize: '12px',
                    width: '180px',
                  }}
                />

                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#cbd5e1', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    data-testid="checkbox-missing-only"
                    checked={showMissingOnly}
                    onChange={(e) => setShowMissingOnly(e.target.checked)}
                  />
                  Untranslated Only
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  data-testid="btn-save-editor"
                  onClick={handleSaveEditor}
                  disabled={Object.keys(pendingEdits).length === 0}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: Object.keys(pendingEdits).length > 0 ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255, 255, 255, 0.08)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '12px',
                    cursor: Object.keys(pendingEdits).length > 0 ? 'pointer' : 'not-allowed',
                  }}
                >
                  Save Changes ({Object.keys(pendingEdits).length}) 💾
                </button>
              </div>
            </div>

            {/* 2-Column Table */}
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: 'rgba(0, 0, 0, 0.4)', color: '#94a3b8', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <th style={{ padding: '10px 14px', width: '22%' }}>Key</th>
                    <th style={{ padding: '10px 14px', width: '38%' }}>English Base Reference</th>
                    <th style={{ padding: '10px 14px', width: '40%' }}>{editorLocale.toUpperCase()} Translation</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((r) => {
                    const currentVal = pendingEdits[r.key] !== undefined ? pendingEdits[r.key] : r.currentValue;
                    const isPending = pendingEdits[r.key] !== undefined;

                    return (
                      <tr
                        key={r.key}
                        data-testid={`editor-row-${r.key}`}
                        style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                          background: isPending ? 'rgba(56, 189, 248, 0.05)' : 'transparent',
                        }}
                      >
                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: '#93c5fd', verticalAlign: 'top' }}>
                          {r.key}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#cbd5e1', verticalAlign: 'top' }}>
                          {r.baseValue}
                        </td>
                        <td style={{ padding: '8px 14px', verticalAlign: 'top' }}>
                          <input
                            type="text"
                            data-testid={`input-translation-${r.key}`}
                            value={currentVal}
                            placeholder={`Translate into ${editorLocale.toUpperCase()}…`}
                            onChange={(e) => handleEditChange(r.key, e.target.value)}
                            style={{
                              width: '100%',
                              background: 'rgba(0, 0, 0, 0.3)',
                              border: `1px solid ${!currentVal ? 'rgba(239, 68, 68, 0.4)' : isPending ? 'rgba(56, 189, 248, 0.5)' : 'rgba(255, 255, 255, 0.1)'}`,
                              borderRadius: '6px',
                              padding: '6px 10px',
                              color: '#fff',
                              fontSize: '12px',
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ─── TAB 3: UPLOAD / PASTE JSON ───────────────────────────────────── */}
        {activeTab === 'UPLOAD' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto', flex: 1 }}>
            {/* Target Select & Dropzone */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.03)',
                padding: '14px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <div>
                <label style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                  Target Language:
                </label>
                <select
                  data-testid="select-upload-locale"
                  value={uploadLocale}
                  onChange={(e) => setUploadLocale(e.target.value)}
                  style={{
                    background: '#0f172a',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    color: '#fff',
                    fontSize: '12px',
                  }}
                >
                  {GLOBAL_LANGUAGE_CATALOG.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flagEmoji} {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ flex: 1, minWidth: '240px' }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".json"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  data-testid="btn-choose-json-file"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px dashed rgba(56, 189, 248, 0.4)',
                    background: 'rgba(56, 189, 248, 0.05)',
                    color: '#38bdf8',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  📁 Drop or Select Translation JSON File
                </button>
              </div>
            </div>

            {/* Raw JSON Code Area */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <label style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>
                Raw JSON Editor (Cut &amp; Paste):
              </label>
              <textarea
                data-testid="textarea-raw-json"
                value={rawJsonInput}
                onChange={(e) => setRawJsonInput(e.target.value)}
                placeholder='{\n  "app.title": "My Translated Title",\n  "common.save": "Guardar"\n}'
                rows={10}
                style={{
                  width: '100%',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '10px',
                  padding: '12px',
                  color: '#93c5fd',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  resize: 'vertical',
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                data-testid="btn-validate-json"
                onClick={handleValidateRawJson}
                disabled={!rawJsonInput.trim()}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Validate JSON Keys 🔍
              </button>

              <button
                type="button"
                data-testid="btn-apply-json"
                onClick={handleApplyUpload}
                disabled={!rawJsonInput.trim()}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Apply &amp; Install Pack ⚡
              </button>
            </div>

            {/* Validation Report Card */}
            {validationReport && (
              <div
                data-testid="validation-report-card"
                style={{
                  background: validationReport.valid ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${validationReport.valid ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  borderRadius: '12px',
                  padding: '14px',
                  fontSize: '12px',
                }}
              >
                <div style={{ fontWeight: 700, color: validationReport.valid ? '#6ee7b7' : '#fca5a5', marginBottom: '8px' }}>
                  {validationReport.valid ? '✓ Validation Passed' : '⚠️ Validation Errors Detected'}
                </div>

                <div style={{ display: 'flex', gap: '20px', color: '#cbd5e1', marginBottom: '8px' }}>
                  <span>Coverage: <strong>{validationReport.coveragePct}%</strong></span>
                  <span>Translated: <strong>{validationReport.translatedKeys} / {validationReport.totalBaseKeys}</strong></span>
                  <span>Missing Keys: <strong>{validationReport.missingKeys.length}</strong></span>
                </div>

                {validationReport.missingKeys.length > 0 && (
                  <div style={{ fontSize: '11px', color: '#fca5a5' }}>
                    Missing Keys: {validationReport.missingKeys.slice(0, 5).join(', ')}
                    {validationReport.missingKeys.length > 5 && ` + ${validationReport.missingKeys.length - 5} more`}
                  </div>
                )}

                {validationReport.errors.length > 0 && (
                  <div style={{ fontSize: '11px', color: '#fca5a5', marginTop: '6px' }}>
                    Errors: {validationReport.errors.join('; ')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
